import type {
  LongreadTestResultsModalArgs,
  LongreadTestResultsPercentTone
} from './LongreadTestResultsModal'
import type {
  TestAttemptOption,
  TestAttemptOptionTone,
  TestAttemptQuestion,
  TestAttemptQuestionTone
} from 'shared/pages/testsAdmin/components/TestAttemptQuestionsSection'
import { resolvedRemoteImageSrc } from 'shared/utils/resolveFilesBurgdevUrl'
import { answerModeFromQuestionType } from 'shared/modals/testCreate/questionTypes'
import {
  pickCorrectOptionExplanation,
  pickShowCorrectAnswersFromTestData,
  pickShowExplanationsTypeFromTestData,
  shouldShowExplanations
} from 'shared/modals/testCreate/testQuestionExplanation'

type UnknownRecord = Record<string, unknown>

function firstFiniteNumber(...candidates: unknown[]): number | null {
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i]
    if (v == null || v === '') {
      continue
    }
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) {
      return n
    }
  }
  return null
}

function pickLocalizedText(value: unknown, lang: string): string {
  if (value == null) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (!Array.isArray(value)) {
    return ''
  }
  const parts = value as { lang?: string; name?: string; text?: string }[]
  const pick = (code: string) => {
    const row = parts.find(
      (p) => p && String(p.lang ?? '').toLowerCase() === code
    )
    return String(row?.name ?? row?.text ?? '').trim()
  }
  return pick(lang) || pick('ru') || pick('en') || pick(String(parts[0]?.lang ?? '')) || ''
}

function pickAttemptScore(testData: UnknownRecord): number | null {
  const attempt = testData.attempt ?? testData.Attempt
  if (attempt == null || typeof attempt !== 'object') {
    return null
  }
  const raw = (attempt as UnknownRecord).score ?? (attempt as UnknownRecord).Score
  if (raw == null || raw === '') {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

function pickPassingPercent(testData: UnknownRecord): number | null {
  return firstFiniteNumber(
    testData.passingPercent,
    testData.passingScorePercent,
    testData.passingScore,
    testData.PassingPercent,
    testData.PassingScore
  )
}

function pickQuestionTotal(testData: UnknownRecord): number {
  const n = firstFiniteNumber(
    testData.numberOfQuestions,
    testData.NumberOfQuestions
  )
  if (n != null) {
    return Math.max(1, Math.floor(n))
  }
  const q = testData.questions ?? testData.Questions
  if (Array.isArray(q) && q.length > 0) {
    return q.length
  }
  return 1
}

function pickAttemptCurrent(testData: UnknownRecord): number {
  const attempt = testData.attempt ?? testData.Attempt
  if (attempt == null || typeof attempt !== 'object') {
    return 1
  }
  const n = firstFiniteNumber(
    (attempt as UnknownRecord).currentAttempt,
    (attempt as UnknownRecord).CurrentAttempt
  )
  return n != null && n > 0 ? Math.floor(n) : 1
}

function pickAttemptTotal(testData: UnknownRecord): number {
  const n = firstFiniteNumber(
    testData.numberOfAttempts,
    testData.NumberOfAttempts,
    testData.maxAttempts,
    testData.MaxAttempts,
    testData.attemptsLimit,
    testData.AttemptsLimit
  )
  return n != null && n > 0 ? Math.floor(n) : 3
}

function coerceBool(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') {
    return true
  }
  return false
}

function pickAttemptAnswerCorrectnessField(a: UnknownRecord): boolean | null {
  if (!a || typeof a !== 'object') {
    return null
  }
  if (Object.prototype.hasOwnProperty.call(a, 'isCorrectAnswer')) {
    const raw = a.isCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  if (Object.prototype.hasOwnProperty.call(a, 'IsCorrectAnswer')) {
    const raw = a.IsCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  return null
}

function pickOptionCorrectFromDefinition(opt: UnknownRecord): boolean | null {
  if (!opt || typeof opt !== 'object') {
    return null
  }
  if (Object.prototype.hasOwnProperty.call(opt, 'isCorrectAnswer')) {
    const raw = opt.isCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  if (Object.prototype.hasOwnProperty.call(opt, 'IsCorrectAnswer')) {
    const raw = opt.IsCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  return null
}

type AttemptAnswerResolution = { row: UnknownRecord; matchedById: boolean }

function resolveAttemptAnswerRowForOption(
  attemptAnswers: UnknownRecord[] | null,
  opt: UnknownRecord,
  optionIndex: number
): AttemptAnswerResolution | null {
  if (!attemptAnswers || attemptAnswers.length === 0) {
    return null
  }
  const optId = String(opt.id ?? opt.Id ?? '').trim()
  if (optId !== '') {
    const hit = attemptAnswers.find((a) => {
      if (!a || typeof a !== 'object') {
        return false
      }
      const row = a as UnknownRecord
      const aid = String(
        row.answerId ??
          row.AnswerId ??
          row.answerOptionId ??
          row.AnswerOptionId ??
          row.optionId ??
          row.OptionId ??
          row.id ??
          row.Id ??
          ''
      ).trim()
      return aid !== '' && aid === optId
    })
    if (hit && typeof hit === 'object') {
      return { row: hit as UnknownRecord, matchedById: true }
    }
  }
  const byIndex = attemptAnswers[optionIndex]
  if (byIndex && typeof byIndex === 'object') {
    return { row: byIndex as UnknownRecord, matchedById: false }
  }
  return null
}

function pickSelectedFromAttemptRow(att: UnknownRecord, matchedById: boolean): boolean {
  if (
    Object.prototype.hasOwnProperty.call(att, 'selected') ||
    Object.prototype.hasOwnProperty.call(att, 'Selected')
  ) {
    return coerceBool(att.selected ?? att.Selected)
  }
  return matchedById
}

function buildOptionTone(
  opt: UnknownRecord,
  attemptAnswers: UnknownRecord[] | null,
  optionIndex: number
): TestAttemptOptionTone {
  const resolved = resolveAttemptAnswerRowForOption(attemptAnswers, opt, optionIndex)
  if (!resolved) {
    return 'neutral'
  }
  const selected = pickSelectedFromAttemptRow(resolved.row, resolved.matchedById)
  if (!selected) {
    return 'neutral'
  }

  const att = resolved.row
  let isCorrectAnswer: boolean | null = pickAttemptAnswerCorrectnessField(att)
  if (isCorrectAnswer === null) {
    isCorrectAnswer = pickOptionCorrectFromDefinition(opt)
  }
  if (isCorrectAnswer === true) {
    return 'correct'
  }
  if (isCorrectAnswer === false) {
    return 'incorrect'
  }
  return 'neutral'
}

function fileImageUrl(f: unknown): string | undefined {
  if (f == null || typeof f !== 'object') {
    return undefined
  }
  const row = f as UnknownRecord
  const u = row.url ?? row.Url ?? row.URL
  const raw = typeof u === 'string' ? u.trim() : ''
  return resolvedRemoteImageSrc(raw)
}

function findAttemptRow(
  attempt: UnknownRecord,
  questionId: string,
  index: number
): UnknownRecord | null {
  const qs = attempt.questions ?? attempt.Questions
  if (!Array.isArray(qs)) {
    return null
  }
  const qid = questionId.trim().toLowerCase()
  let row =
    qid !== ''
      ? (qs as UnknownRecord[]).find(
          (q) =>
            String(q?.id ?? q?.Id ?? '')
              .trim()
              .toLowerCase() === qid
        ) ?? null
      : null
  if (!row && qs[index]) {
    row = qs[index] as UnknownRecord
  }
  return row
}

function resolveAttemptAnswersForQuestion(
  testData: UnknownRecord,
  attemptObj: UnknownRecord | null,
  questionId: string,
  index: number
): UnknownRecord[] | null {
  if (attemptObj) {
    const attemptRow = findAttemptRow(attemptObj, questionId, index)
    if (attemptRow) {
      const ans = attemptRow.answers ?? attemptRow.Answers
      if (Array.isArray(ans)) {
        return ans as UnknownRecord[]
      }
    }
  }

  const topLevel = testData.attemptAnswers ?? testData.AttemptAnswers
  if (!Array.isArray(topLevel)) {
    return null
  }
  const qid = questionId.trim().toLowerCase()
  for (let i = 0; i < topLevel.length; i += 1) {
    const item = topLevel[i]
    if (!item || typeof item !== 'object') {
      continue
    }
    const rec = item as UnknownRecord
    const rid = String(rec.id ?? rec.Id ?? '').trim().toLowerCase()
    if (qid !== '' && rid === qid) {
      const ans = rec.answers ?? rec.Answers
      if (Array.isArray(ans)) {
        return ans as UnknownRecord[]
      }
    }
  }
  if (topLevel[index] && typeof topLevel[index] === 'object') {
    const rec = topLevel[index] as UnknownRecord
    const ans = rec.answers ?? rec.Answers
    if (Array.isArray(ans)) {
      return ans as UnknownRecord[]
    }
  }
  return null
}

function mergeAnswerOptionForResults(
  freshOpt: UnknownRecord,
  passedOpt: UnknownRecord | undefined,
  lang: string
): UnknownRecord {
  if (!passedOpt) {
    return freshOpt
  }
  const freshExpl = pickLocalizedText(
    freshOpt.explanatoryText ?? freshOpt.ExplanatoryText,
    lang
  ).trim()
  if (freshExpl) {
    return freshOpt
  }
  const passedExpl = passedOpt.explanatoryText ?? passedOpt.ExplanatoryText
  if (passedExpl == null) {
    return freshOpt
  }
  return {
    ...freshOpt,
    explanatoryText: passedExpl,
    ExplanatoryText: passedExpl
  }
}

function mergeQuestionsForResultsModal(
  freshQs: unknown,
  passedQs: unknown,
  lang: string
): unknown {
  if (!Array.isArray(freshQs)) {
    return passedQs ?? freshQs
  }
  if (!Array.isArray(passedQs)) {
    return freshQs
  }
  const passedList = passedQs as UnknownRecord[]
  const passedById = new Map<string, UnknownRecord>()
  passedList.forEach((q, i) => {
    const id = String(q?.id ?? q?.Id ?? '').trim()
    if (id) {
      passedById.set(id.toLowerCase(), q)
    }
    passedById.set(`__idx_${i}`, q)
  })

  return (freshQs as UnknownRecord[]).map((fq, index) => {
    const fid = String(fq?.id ?? fq?.Id ?? '').trim()
    const passedQ =
      (fid ? passedById.get(fid.toLowerCase()) : undefined) ??
      passedById.get(`__idx_${index}`)
    if (!passedQ) {
      return fq
    }
    const freshOpts = fq.answerOptions ?? fq.AnswerOptions
    const passedOpts = passedQ.answerOptions ?? passedQ.AnswerOptions
    if (!Array.isArray(freshOpts)) {
      return fq
    }
    const passedOptList = Array.isArray(passedOpts)
      ? (passedOpts as UnknownRecord[])
      : []
    const mergedOpts = freshOpts.map((opt, oi) => {
      const fo = opt as UnknownRecord
      const po = passedOptList[oi]
      const poById =
        po ??
        passedOptList.find(
          (p) =>
            String(p?.id ?? p?.Id ?? '').trim() !== '' &&
            String(p?.id ?? p?.Id ?? '').trim() ===
              String(fo?.id ?? fo?.Id ?? '').trim()
        )
      return mergeAnswerOptionForResults(fo, poById, lang)
    })
    return {
      ...fq,
      answerOptions: mergedOpts,
      AnswerOptions: mergedOpts
    }
  })
}

export function mergeTestItemForResultsModal(
  passed: UnknownRecord | null | undefined,
  fresh: UnknownRecord,
  lang: string
): UnknownRecord {
  if (!passed) {
    return fresh
  }
  const mergedQuestions = mergeQuestionsForResultsModal(
    fresh.questions ?? fresh.Questions,
    passed.questions ?? passed.Questions,
    lang
  )
  return {
    ...fresh,
    questions: mergedQuestions,
    Questions: mergedQuestions,
    attempt:
      fresh.attempt ??
      fresh.Attempt ??
      passed.attempt ??
      passed.Attempt,
    Attempt:
      fresh.attempt ??
      fresh.Attempt ??
      passed.attempt ??
      passed.Attempt,
    showCorrectAnswers:
      fresh.showCorrectAnswers ??
      fresh.ShowCorrectAnswers ??
      passed.showCorrectAnswers ??
      passed.ShowCorrectAnswers,
    showExplanationsType:
      fresh.showExplanationsType ??
      fresh.ShowExplanationsType ??
      passed.showExplanationsType ??
      passed.ShowExplanationsType
  }
}

function shouldShowQuestionReviewInResultsModal(testData: UnknownRecord): boolean {
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object' ? (attempt as UnknownRecord) : null

  const showResultsAfterAnswer = pickShowCorrectAnswersFromTestData(testData)
  const serverHasCorrectness =
    attemptObj != null && attemptAnyAnswerHasCorrectnessFlag(attemptObj)

  return showResultsAfterAnswer || serverHasCorrectness
}

function shouldShowExplanationsInResultsModal(
  testData: UnknownRecord,
  scorePercentRounded: number,
  passingPercent: number
): boolean {
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object' ? (attempt as UnknownRecord) : null
  const showResultsAfterAnswer = pickShowCorrectAnswersFromTestData(testData)
  const serverHasCorrectness =
    attemptObj != null && attemptAnyAnswerHasCorrectnessFlag(attemptObj)

  return shouldShowExplanations({
    showCorrectAnswers: showResultsAfterAnswer || serverHasCorrectness,
    explainType: pickShowExplanationsTypeFromTestData(testData),
    allAnswered: true,
    isResultsView: true,
    passed: scorePercentRounded >= passingPercent
  })
}

function attemptAnyAnswerHasCorrectnessFlag(attempt: UnknownRecord): boolean {
  const qs = attempt.questions ?? attempt.Questions
  if (!Array.isArray(qs)) {
    return false
  }
  for (let qi = 0; qi < qs.length; qi += 1) {
    const q = qs[qi] as UnknownRecord | undefined
    if (!q || typeof q !== 'object') {
      continue
    }
    const ans = q.answers ?? q.Answers
    if (!Array.isArray(ans)) {
      continue
    }
    for (let ai = 0; ai < ans.length; ai += 1) {
      const a = ans[ai]
      if (!a || typeof a !== 'object') {
        continue
      }
      if (pickAttemptAnswerCorrectnessField(a as UnknownRecord) != null) {
        return true
      }
    }
  }
  return false
}

function deriveQuestionTone(
  options: TestAttemptOption[]
): TestAttemptQuestionTone {
  const hasIncorrect = options.some((o) => o.tone === 'incorrect')
  if (hasIncorrect) {
    const hasCorrect = options.some((o) => o.tone === 'correct')
    return hasCorrect ? 'partial' : 'incorrect'
  }
  return 'correct'
}

function mapQuestions(
  testData: UnknownRecord,
  lang: string,
  showExplanations: boolean
): TestAttemptQuestion[] {
  const questions = testData.questions ?? testData.Questions
  if (!Array.isArray(questions) || questions.length === 0) {
    return []
  }
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object'
      ? (attempt as UnknownRecord)
      : null

  return (questions as UnknownRecord[]).map((q, index) => {
    const qid = String(q.id ?? q.Id ?? `q-${index}`).trim()
    const attemptAnswers = resolveAttemptAnswersForQuestion(
      testData,
      attemptObj,
      qid,
      index
    )
    const rawOpts = q.answerOptions ?? q.AnswerOptions
    const opts = Array.isArray(rawOpts) ? (rawOpts as UnknownRecord[]) : []

    const options: TestAttemptOption[] = opts.map((opt, oi) => {
      const title = pickLocalizedText(opt.text ?? opt.Text, lang)
      const description = pickLocalizedText(
        opt.description ?? opt.Description,
        lang
      )
      const files = opt.files ?? opt.Files
      const imageUrl =
        Array.isArray(files) && files.length > 0
          ? fileImageUrl(files[0])
          : undefined
      const resolved = resolveAttemptAnswerRowForOption(attemptAnswers, opt, oi)
      const selected = resolved
        ? pickSelectedFromAttemptRow(resolved.row, resolved.matchedById)
        : false

      return {
        id: String(opt.id ?? opt.Id ?? `${qid}-o-${oi}`),
        title,
        description,
        imageUrl,
        tone: buildOptionTone(opt, attemptAnswers, oi),
        selected,
      }
    })

    return {
      id: qid || `q-${index}`,
      index: index + 1,
      title: pickLocalizedText(q.text ?? q.Text, lang),
      tone: deriveQuestionTone(options),
      answerMode: answerModeFromQuestionType(q.type ?? q.Type),
      options,
      explanation: (() => {
        if (!showExplanations) {
          return undefined
        }
        const text = pickCorrectOptionExplanation(rawOpts, attemptAnswers, lang)
        return text || undefined
      })(),
    }
  })
}

export function buildLongreadTestResultsModalArgsFromTestItem(
  testData: UnknownRecord,
  lang: string
): LongreadTestResultsModalArgs {
  const questionTotal = pickQuestionTotal(testData)
  const passing = pickPassingPercent(testData) ?? 0
  let percent = pickAttemptScore(testData)
  if (percent == null) {
    percent = 0
  }
  const percentRounded = Math.min(100, Math.max(0, Math.round(percent)))
  let percentTone: LongreadTestResultsPercentTone = 'danger'
  if (percentRounded >= passing) {
    percentTone = 'success'
  }

  const correctCount = Math.round((questionTotal * percentRounded) / 100)
  const showQuestionReview = shouldShowQuestionReviewInResultsModal(testData)
  const showExplanations = shouldShowExplanationsInResultsModal(
    testData,
    percentRounded,
    passing
  )
  const mappedQuestions = showQuestionReview
    ? mapQuestions(testData, lang, showExplanations)
    : []

  return {
    percent: percentRounded,
    percentTone,
    attemptCurrent: pickAttemptCurrent(testData),
    attemptTotal: pickAttemptTotal(testData),
    correctCount,
    questionTotal,
    questions: showQuestionReview ? mappedQuestions : undefined,
    onReturnToLongread: () => {}
  }
}
import type {
  LongreadTestResultsModalArgs,
  LongreadTestResultsPercentTone
} from './LongreadTestResultsModal'
import type {
  TestAttemptOption,
  TestAttemptOptionTone,
  TestAttemptQuestion,
  TestAttemptQuestionTone
} from 'shared/pages/testsAdmin/components/TestAttemptQuestionsSection'
import { resolvedRemoteImageSrc } from 'shared/utils/resolveFilesBurgdevUrl'
import { answerModeFromQuestionType } from 'shared/modals/testCreate/questionTypes'
import {
  pickCorrectOptionExplanation,
  pickShowCorrectAnswersFromTestData,
  pickShowExplanationsTypeFromTestData,
  shouldShowExplanations
} from 'shared/modals/testCreate/testQuestionExplanation'

type UnknownRecord = Record<string, unknown>

function firstFiniteNumber(...candidates: unknown[]): number | null {
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i]
    if (v == null || v === '') {
      continue
    }
    const n = typeof v === 'number' ? v : Number(v)
    if (Number.isFinite(n)) {
      return n
    }
  }
  return null
}

function pickLocalizedText(value: unknown, lang: string): string {
  if (value == null) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (!Array.isArray(value)) {
    return ''
  }
  const parts = value as { lang?: string; name?: string; text?: string }[]
  const pick = (code: string) => {
    const row = parts.find(
      (p) => p && String(p.lang ?? '').toLowerCase() === code
    )
    return String(row?.name ?? row?.text ?? '').trim()
  }
  return pick(lang) || pick('ru') || pick('en') || pick(String(parts[0]?.lang ?? '')) || ''
}

function pickAttemptScore(testData: UnknownRecord): number | null {
  const attempt = testData.attempt ?? testData.Attempt
  if (attempt == null || typeof attempt !== 'object') {
    return null
  }
  const raw = (attempt as UnknownRecord).score ?? (attempt as UnknownRecord).Score
  if (raw == null || raw === '') {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(n) ? n : null
}

function pickPassingPercent(testData: UnknownRecord): number | null {
  return firstFiniteNumber(
    testData.passingPercent,
    testData.passingScorePercent,
    testData.passingScore,
    testData.PassingPercent,
    testData.PassingScore
  )
}

function pickQuestionTotal(testData: UnknownRecord): number {
  const n = firstFiniteNumber(
    testData.numberOfQuestions,
    testData.NumberOfQuestions
  )
  if (n != null) {
    return Math.max(1, Math.floor(n))
  }
  const q = testData.questions ?? testData.Questions
  if (Array.isArray(q) && q.length > 0) {
    return q.length
  }
  return 1
}

function pickAttemptCurrent(testData: UnknownRecord): number {
  const attempt = testData.attempt ?? testData.Attempt
  if (attempt == null || typeof attempt !== 'object') {
    return 1
  }
  const n = firstFiniteNumber(
    (attempt as UnknownRecord).currentAttempt,
    (attempt as UnknownRecord).CurrentAttempt
  )
  return n != null && n > 0 ? Math.floor(n) : 1
}

function pickAttemptTotal(testData: UnknownRecord): number {
  const n = firstFiniteNumber(
    testData.numberOfAttempts,
    testData.NumberOfAttempts,
    testData.maxAttempts,
    testData.MaxAttempts,
    testData.attemptsLimit,
    testData.AttemptsLimit
  )
  return n != null && n > 0 ? Math.floor(n) : 3
}

function coerceBool(v: unknown): boolean {
  if (v === true || v === 1 || v === '1') {
    return true
  }
  return false
}

function pickAttemptAnswerCorrectnessField(a: UnknownRecord): boolean | null {
  if (!a || typeof a !== 'object') {
    return null
  }
  if (Object.prototype.hasOwnProperty.call(a, 'isCorrectAnswer')) {
    const raw = a.isCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  if (Object.prototype.hasOwnProperty.call(a, 'IsCorrectAnswer')) {
    const raw = a.IsCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  return null
}

function pickOptionCorrectFromDefinition(opt: UnknownRecord): boolean | null {
  if (!opt || typeof opt !== 'object') {
    return null
  }
  if (Object.prototype.hasOwnProperty.call(opt, 'isCorrectAnswer')) {
    const raw = opt.isCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  if (Object.prototype.hasOwnProperty.call(opt, 'IsCorrectAnswer')) {
    const raw = opt.IsCorrectAnswer
    if (raw === null || raw === undefined) {
      return null
    }
    return coerceBool(raw)
  }
  return null
}

type AttemptAnswerResolution = { row: UnknownRecord; matchedById: boolean }

function resolveAttemptAnswerRowForOption(
  attemptAnswers: UnknownRecord[] | null,
  opt: UnknownRecord,
  optionIndex: number
): AttemptAnswerResolution | null {
  if (!attemptAnswers || attemptAnswers.length === 0) {
    return null
  }
  const optId = String(opt.id ?? opt.Id ?? '').trim()
  if (optId !== '') {
    const hit = attemptAnswers.find((a) => {
      if (!a || typeof a !== 'object') {
        return false
      }
      const row = a as UnknownRecord
      const aid = String(
        row.answerId ??
          row.AnswerId ??
          row.answerOptionId ??
          row.AnswerOptionId ??
          row.optionId ??
          row.OptionId ??
          row.id ??
          row.Id ??
          ''
      ).trim()
      return aid !== '' && aid === optId
    })
    if (hit && typeof hit === 'object') {
      return { row: hit as UnknownRecord, matchedById: true }
    }
  }
  const byIndex = attemptAnswers[optionIndex]
  if (byIndex && typeof byIndex === 'object') {
    return { row: byIndex as UnknownRecord, matchedById: false }
  }
  return null
}

function pickSelectedFromAttemptRow(att: UnknownRecord, matchedById: boolean): boolean {
  if (
    Object.prototype.hasOwnProperty.call(att, 'selected') ||
    Object.prototype.hasOwnProperty.call(att, 'Selected')
  ) {
    return coerceBool(att.selected ?? att.Selected)
  }
  return matchedById
}

function buildOptionTone(
  opt: UnknownRecord,
  attemptAnswers: UnknownRecord[] | null,
  optionIndex: number
): TestAttemptOptionTone {
  const resolved = resolveAttemptAnswerRowForOption(attemptAnswers, opt, optionIndex)
  if (!resolved) {
    return 'neutral'
  }
  const selected = pickSelectedFromAttemptRow(resolved.row, resolved.matchedById)
  if (!selected) {
    return 'neutral'
  }

  const att = resolved.row
  let isCorrectAnswer: boolean | null = pickAttemptAnswerCorrectnessField(att)
  if (isCorrectAnswer === null) {
    isCorrectAnswer = pickOptionCorrectFromDefinition(opt)
  }
  if (isCorrectAnswer === true) {
    return 'correct'
  }
  if (isCorrectAnswer === false) {
    return 'incorrect'
  }
  return 'neutral'
}

function fileImageUrl(f: unknown): string | undefined {
  if (f == null || typeof f !== 'object') {
    return undefined
  }
  const row = f as UnknownRecord
  const u = row.url ?? row.Url ?? row.URL
  const raw = typeof u === 'string' ? u.trim() : ''
  return resolvedRemoteImageSrc(raw)
}

function findAttemptRow(
  attempt: UnknownRecord,
  questionId: string,
  index: number
): UnknownRecord | null {
  const qs = attempt.questions ?? attempt.Questions
  if (!Array.isArray(qs)) {
    return null
  }
  const qid = questionId.trim().toLowerCase()
  let row =
    qid !== ''
      ? (qs as UnknownRecord[]).find(
          (q) =>
            String(q?.id ?? q?.Id ?? '')
              .trim()
              .toLowerCase() === qid
        ) ?? null
      : null
  if (!row && qs[index]) {
    row = qs[index] as UnknownRecord
  }
  return row
}

function resolveAttemptAnswersForQuestion(
  testData: UnknownRecord,
  attemptObj: UnknownRecord | null,
  questionId: string,
  index: number
): UnknownRecord[] | null {
  if (attemptObj) {
    const attemptRow = findAttemptRow(attemptObj, questionId, index)
    if (attemptRow) {
      const ans = attemptRow.answers ?? attemptRow.Answers
      if (Array.isArray(ans)) {
        return ans as UnknownRecord[]
      }
    }
  }

  const topLevel = testData.attemptAnswers ?? testData.AttemptAnswers
  if (!Array.isArray(topLevel)) {
    return null
  }
  const qid = questionId.trim().toLowerCase()
  for (let i = 0; i < topLevel.length; i += 1) {
    const item = topLevel[i]
    if (!item || typeof item !== 'object') {
      continue
    }
    const rec = item as UnknownRecord
    const rid = String(rec.id ?? rec.Id ?? '').trim().toLowerCase()
    if (qid !== '' && rid === qid) {
      const ans = rec.answers ?? rec.Answers
      if (Array.isArray(ans)) {
        return ans as UnknownRecord[]
      }
    }
  }
  if (topLevel[index] && typeof topLevel[index] === 'object') {
    const rec = topLevel[index] as UnknownRecord
    const ans = rec.answers ?? rec.Answers
    if (Array.isArray(ans)) {
      return ans as UnknownRecord[]
    }
  }
  return null
}

function mergeAnswerOptionForResults(
  freshOpt: UnknownRecord,
  passedOpt: UnknownRecord | undefined,
  lang: string
): UnknownRecord {
  if (!passedOpt) {
    return freshOpt
  }
  const freshExpl = pickLocalizedText(
    freshOpt.explanatoryText ?? freshOpt.ExplanatoryText,
    lang
  ).trim()
  if (freshExpl) {
    return freshOpt
  }
  const passedExpl = passedOpt.explanatoryText ?? passedOpt.ExplanatoryText
  if (passedExpl == null) {
    return freshOpt
  }
  return {
    ...freshOpt,
    explanatoryText: passedExpl,
    ExplanatoryText: passedExpl
  }
}

function mergeQuestionsForResultsModal(
  freshQs: unknown,
  passedQs: unknown,
  lang: string
): unknown {
  if (!Array.isArray(freshQs)) {
    return passedQs ?? freshQs
  }
  if (!Array.isArray(passedQs)) {
    return freshQs
  }
  const passedList = passedQs as UnknownRecord[]
  const passedById = new Map<string, UnknownRecord>()
  passedList.forEach((q, i) => {
    const id = String(q?.id ?? q?.Id ?? '').trim()
    if (id) {
      passedById.set(id.toLowerCase(), q)
    }
    passedById.set(`__idx_${i}`, q)
  })

  return (freshQs as UnknownRecord[]).map((fq, index) => {
    const fid = String(fq?.id ?? fq?.Id ?? '').trim()
    const passedQ =
      (fid ? passedById.get(fid.toLowerCase()) : undefined) ??
      passedById.get(`__idx_${index}`)
    if (!passedQ) {
      return fq
    }
    const freshOpts = fq.answerOptions ?? fq.AnswerOptions
    const passedOpts = passedQ.answerOptions ?? passedQ.AnswerOptions
    if (!Array.isArray(freshOpts)) {
      return fq
    }
    const passedOptList = Array.isArray(passedOpts)
      ? (passedOpts as UnknownRecord[])
      : []
    const mergedOpts = freshOpts.map((opt, oi) => {
      const fo = opt as UnknownRecord
      const po = passedOptList[oi]
      const poById =
        po ??
        passedOptList.find(
          (p) =>
            String(p?.id ?? p?.Id ?? '').trim() !== '' &&
            String(p?.id ?? p?.Id ?? '').trim() ===
              String(fo?.id ?? fo?.Id ?? '').trim()
        )
      return mergeAnswerOptionForResults(fo, poById, lang)
    })
    return {
      ...fq,
      answerOptions: mergedOpts,
      AnswerOptions: mergedOpts
    }
  })
}

export function mergeTestItemForResultsModal(
  passed: UnknownRecord | null | undefined,
  fresh: UnknownRecord,
  lang: string
): UnknownRecord {
  if (!passed) {
    return fresh
  }
  const mergedQuestions = mergeQuestionsForResultsModal(
    fresh.questions ?? fresh.Questions,
    passed.questions ?? passed.Questions,
    lang
  )
  return {
    ...fresh,
    questions: mergedQuestions,
    Questions: mergedQuestions,
    attempt:
      fresh.attempt ??
      fresh.Attempt ??
      passed.attempt ??
      passed.Attempt,
    Attempt:
      fresh.attempt ??
      fresh.Attempt ??
      passed.attempt ??
      passed.Attempt,
    showCorrectAnswers:
      fresh.showCorrectAnswers ??
      fresh.ShowCorrectAnswers ??
      passed.showCorrectAnswers ??
      passed.ShowCorrectAnswers,
    showExplanationsType:
      fresh.showExplanationsType ??
      fresh.ShowExplanationsType ??
      passed.showExplanationsType ??
      passed.ShowExplanationsType
  }
}

function shouldShowQuestionReviewInResultsModal(testData: UnknownRecord): boolean {
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object' ? (attempt as UnknownRecord) : null

  const showResultsAfterAnswer = pickShowCorrectAnswersFromTestData(testData)
  const serverHasCorrectness =
    attemptObj != null && attemptAnyAnswerHasCorrectnessFlag(attemptObj)

  return showResultsAfterAnswer || serverHasCorrectness
}

function shouldShowExplanationsInResultsModal(
  testData: UnknownRecord,
  scorePercentRounded: number,
  passingPercent: number
): boolean {
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object' ? (attempt as UnknownRecord) : null
  const showResultsAfterAnswer = pickShowCorrectAnswersFromTestData(testData)
  const serverHasCorrectness =
    attemptObj != null && attemptAnyAnswerHasCorrectnessFlag(attemptObj)

  return shouldShowExplanations({
    showCorrectAnswers: showResultsAfterAnswer || serverHasCorrectness,
    explainType: pickShowExplanationsTypeFromTestData(testData),
    allAnswered: true,
    isResultsView: true,
    passed: scorePercentRounded >= passingPercent
  })
}

function attemptAnyAnswerHasCorrectnessFlag(attempt: UnknownRecord): boolean {
  const qs = attempt.questions ?? attempt.Questions
  if (!Array.isArray(qs)) {
    return false
  }
  for (let qi = 0; qi < qs.length; qi += 1) {
    const q = qs[qi] as UnknownRecord | undefined
    if (!q || typeof q !== 'object') {
      continue
    }
    const ans = q.answers ?? q.Answers
    if (!Array.isArray(ans)) {
      continue
    }
    for (let ai = 0; ai < ans.length; ai += 1) {
      const a = ans[ai]
      if (!a || typeof a !== 'object') {
        continue
      }
      if (pickAttemptAnswerCorrectnessField(a as UnknownRecord) != null) {
        return true
      }
    }
  }
  return false
}

function deriveQuestionTone(
  options: TestAttemptOption[]
): TestAttemptQuestionTone {
  const hasIncorrect = options.some((o) => o.tone === 'incorrect')
  if (hasIncorrect) {
    const hasCorrect = options.some((o) => o.tone === 'correct')
    return hasCorrect ? 'partial' : 'incorrect'
  }
  return 'correct'
}

function mapQuestions(
  testData: UnknownRecord,
  lang: string,
  showExplanations: boolean
): TestAttemptQuestion[] {
  const questions = testData.questions ?? testData.Questions
  if (!Array.isArray(questions) || questions.length === 0) {
    return []
  }
  const attempt = testData.attempt ?? testData.Attempt
  const attemptObj =
    attempt != null && typeof attempt === 'object'
      ? (attempt as UnknownRecord)
      : null

  return (questions as UnknownRecord[]).map((q, index) => {
    const qid = String(q.id ?? q.Id ?? `q-${index}`).trim()
    const attemptAnswers = resolveAttemptAnswersForQuestion(
      testData,
      attemptObj,
      qid,
      index
    )
    const rawOpts = q.answerOptions ?? q.AnswerOptions
    const opts = Array.isArray(rawOpts) ? (rawOpts as UnknownRecord[]) : []

    const options: TestAttemptOption[] = opts.map((opt, oi) => {
      const title = pickLocalizedText(opt.text ?? opt.Text, lang)
      const description = pickLocalizedText(
        opt.description ?? opt.Description,
        lang
      )
      const files = opt.files ?? opt.Files
      const imageUrl =
        Array.isArray(files) && files.length > 0
          ? fileImageUrl(files[0])
          : undefined
      const resolved = resolveAttemptAnswerRowForOption(attemptAnswers, opt, oi)
      const selected = resolved
        ? pickSelectedFromAttemptRow(resolved.row, resolved.matchedById)
        : false

      return {
        id: String(opt.id ?? opt.Id ?? `${qid}-o-${oi}`),
        title,
        description,
        imageUrl,
        tone: buildOptionTone(opt, attemptAnswers, oi),
        selected,
      }
    })

    return {
      id: qid || `q-${index}`,
      index: index + 1,
      title: pickLocalizedText(q.text ?? q.Text, lang),
      tone: deriveQuestionTone(options),
      answerMode: answerModeFromQuestionType(q.type ?? q.Type),
      options,
      explanation: (() => {
        if (!showExplanations) {
          return undefined
        }
        const text = pickCorrectOptionExplanation(rawOpts, attemptAnswers, lang)
        return text || undefined
      })(),
    }
  })
}

export function buildLongreadTestResultsModalArgsFromTestItem(
  testData: UnknownRecord,
  lang: string
): LongreadTestResultsModalArgs {
  const questionTotal = pickQuestionTotal(testData)
  const passing = pickPassingPercent(testData) ?? 0
  let percent = pickAttemptScore(testData)
  if (percent == null) {
    percent = 0
  }
  const percentRounded = Math.min(100, Math.max(0, Math.round(percent)))
  let percentTone: LongreadTestResultsPercentTone = 'danger'
  if (percentRounded >= passing) {
    percentTone = 'success'
  }

  const correctCount = Math.round((questionTotal * percentRounded) / 100)
  const showQuestionReview = shouldShowQuestionReviewInResultsModal(testData)
  const showExplanations = shouldShowExplanationsInResultsModal(
    testData,
    percentRounded,
    passing
  )
  const mappedQuestions = showQuestionReview
    ? mapQuestions(testData, lang, showExplanations)
    : []

  return {
    percent: percentRounded,
    percentTone,
    attemptCurrent: pickAttemptCurrent(testData),
    attemptTotal: pickAttemptTotal(testData),
    correctCount,
    questionTotal,
    questions: showQuestionReview ? mappedQuestions : undefined,
    onReturnToLongread: () => {}
  }
}
